import { subtaskProposalsOf, type FactorySubtasks, type SubtaskDecision } from '@shared/factory-subtasks';
import type { TaskEvent } from '@shared/types/tasks';
import { showFoldingCard } from './folding-card';

/** One run's proposed sub-tasks on a task, and what was added (null until someone decides). */
export interface SubtaskProposal {
  /** The factory's message with the factory-subtasks block. */
  event: TaskEvent;
  runNumber: number | null;
  subtasks: FactorySubtasks;
  /** By number (1-based); every one is decided at once. */
  decided: Map<number, SubtaskDecision> | null;
}

/**
 * Every run's proposed sub-tasks in a task's events (oldest first), newest first. A decisions message
 * belongs to the proposal of the run it names; one naming no run, to the newest before it
 * (subtaskProposalsOf, which the Inbox reads them with too).
 */
export function subtaskProposals(events: TaskEvent[]): SubtaskProposal[] {
  const messages = events.filter((e) => e.kind === 'chat' && e.body).map((event) => ({ event, body: event.body, label: event.actor_label }));
  return subtaskProposalsOf(messages)
    .map((p) => ({ event: p.message.event, runNumber: p.run, subtasks: p.subtasks, decided: p.decided }))
    .reverse();
}

/** The id of a task's Proposed sub-tasks section (ProposedSubtasks: the decided ones). */
export const proposedSubtasksAnchorId = (taskId: string) => `task-proposed-subtasks-${taskId}`;

/** The id of one run's proposal card, wherever it shows (To answer while undecided, Proposed sub-tasks once decided). */
export const subtaskProposalId = (eventId: string) => `task-subtask-proposal-${eventId}`;

/** Scrolls to a run's proposal (the pointer line in the comments), opening it if folded; else to the Proposed sub-tasks. */
export function showProposedSubtasks(taskId: string, eventId?: string) {
  if (eventId && showFoldingCard(subtaskProposalId(eventId))) return;
  document.getElementById(proposedSubtasksAnchorId(taskId))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
